/* eslint react-hooks/exhaustive-deps: "warn" */

import { useId } from 'react';

import cn from 'classnames';

import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './AlertRoutingBoard.module.scss';

import type { AlertRoutingBoardProps } from './AlertRoutingBoard.types';

const VIEWBOX_HEIGHT = 500;
const DOT_DURATION = 2.6;

export const AlertRoutingBoard = ({ sourceTitle, sourceMeta, destinations, className }: AlertRoutingBoardProps) => {
  const pathId = useId().replace(/:/g, '');

  const step = VIEWBOX_HEIGHT / destinations.length;
  const paths = destinations.map((destination, index) => {
    const y = step * (index + 0.5);

    return {
      id: `${pathId}-route-${index}`,
      d: `M0,${VIEWBOX_HEIGHT / 2} C50,${VIEWBOX_HEIGHT / 2} 50,${y} 100,${y}`,
      begin: `${((DOT_DURATION / destinations.length) * index).toFixed(2)}s`,
      key: destination.name,
    };
  });

  return (
    <div className={cn(styles.board, className)}>
      <div className={styles.source}>
        <div className={styles.sourceCard}>
          <span className={styles.dot} aria-hidden="true" />

          <div>
            <div className={styles.sourceTitle}>{sourceTitle}</div>
            <div className={styles.sourceMeta}>{sourceMeta}</div>
          </div>
        </div>
      </div>

      <div className={styles.lines} aria-hidden="true">
        <svg viewBox={`0 0 100 ${VIEWBOX_HEIGHT}`} preserveAspectRatio="none">
          {paths.map((path) => (
            <path key={path.key} id={path.id} d={path.d} fill="none" stroke="#2a3054" strokeWidth="1.5" />
          ))}

          {paths.map((path) => (
            <circle key={path.key} r="3.5" fill="#6b78ff">
              <animateMotion dur={`${DOT_DURATION}s`} begin={path.begin} repeatCount="indefinite">
                <mpath href={`#${path.id}`} />
              </animateMotion>
            </circle>
          ))}
        </svg>
      </div>

      <div className={styles.dests}>
        {destinations.map((destination) => (
          <div key={destination.name} className={styles.dest}>
            <div className={styles.destMain}>
              <span className={styles.destName}>
                {destination.name}
                {destination.pill && <span className={styles.pill}>{destination.pill}</span>}
              </span>

              <span className={styles.destDescription}>{destination.description}</span>
            </div>

            <span className={styles.destLinks}>
              {destination.links.map((link) => (
                <a key={link.label} href={link.href} className={styles.destLink}>
                  {link.label}
                  <Icon type="arrow-14" className={styles.destLinkIcon} />
                </a>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
